import { useTranslation } from 'react-i18next';
import { Undo2, Loader2 } from 'lucide-react';
import { trpc } from '@/lib/trpc';

/**
 * Session change log with one-step undo.
 *
 * Structural editing without a way back makes people cautious, which is the
 * opposite of what a preparation mode is for. The log lives in the API process,
 * so it resets when the server restarts — the panel says so rather than letting
 * the accountant discover it at the wrong moment.
 */
export function ChangeLogPanel() {
  const { t } = useTranslation('accounting');
  const utils = trpc.useUtils();
  const { data: changes } = trpc.accountWorkbench.changes.useQuery();

  const undo = trpc.accountWorkbench.undo.useMutation({
    onSuccess: () => {
      utils.account.getTree.invalidate();
      utils.account.list.invalidate();
      utils.accountWorkbench.health.invalidate();
      utils.accountWorkbench.changes.invalidate();
    },
  });

  if (!changes || changes.length === 0) return null;

  return (
    <div className="border-border bg-card border">
      <div className="border-border flex items-center justify-between border-b px-4 py-2">
        <h3 className="text-sm font-medium">{t('changeLog.title')}</h3>
        <span className="text-muted-foreground text-xs">{t('changeLog.sessionOnly')}</span>
      </div>
      <ul className="divide-border divide-y">
        {changes.slice(0, 8).map((change) => (
          <li key={change.id} className="flex items-center gap-3 px-4 py-2">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm">{change.label}</p>
              <p className="text-muted-foreground text-xs tabular-nums">
                {new Date(change.at).toLocaleTimeString()} ·{' '}
                {t('changeLog.accountCount', { count: change.accountCount })}
              </p>
            </div>
            <button
              className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-xs disabled:opacity-50"
              disabled={undo.isPending}
              onClick={() => undo.mutate({ changeId: change.id })}
            >
              {undo.isPending && undo.variables?.changeId === change.id ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Undo2 className="h-3.5 w-3.5" />
              )}
              {t('changeLog.undo')}
            </button>
          </li>
        ))}
      </ul>
      {undo.error && (
        <p className="border-border border-t bg-red-50 px-4 py-2 text-xs text-red-700 dark:bg-red-950/40 dark:text-red-300">
          {undo.error.message}
        </p>
      )}
    </div>
  );
}
